import { type DragEvent, type ReactNode, useRef, useState } from 'react'

import { cn } from '@/utils'

interface AttachmentDropZoneProps {
  onFilesDropped: (files: FileList) => void
  className?: string
  children: ReactNode
}

const carriesFiles = (event: DragEvent<HTMLDivElement>) =>
  event.dataTransfer.types.includes('Files')

export const AttachmentDropZone = ({
  onFilesDropped,
  className,
  children,
}: AttachmentDropZoneProps) => {
  const [isDraggingFiles, setIsDraggingFiles] = useState(false)
  // Dragging over a child fires dragleave on the parent, so count enters
  // instead of trusting a single leave.
  const dragDepth = useRef(0)

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (!carriesFiles(event)) return
    event.preventDefault()
    dragDepth.current += 1
    setIsDraggingFiles(true)
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    // Without this the browser opens the dropped file and leaves the app.
    if (carriesFiles(event)) event.preventDefault()
  }

  // No file gate on the way out: some browsers strip the drag types on
  // dragleave, and one missed decrement leaves the overlay stuck on screen.
  const handleDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setIsDraggingFiles(false)
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    // Dragged text belongs to the textarea, which only gets it when the browser
    // handles the drop itself.
    if (!carriesFiles(event)) return

    event.preventDefault()
    dragDepth.current = 0
    setIsDraggingFiles(false)
    if (event.dataTransfer.files.length > 0) {
      onFilesDropped(event.dataTransfer.files)
    }
  }

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn('relative', className)}
    >
      {children}

      {isDraggingFiles && (
        <div className="pointer-events-none absolute inset-2 flex items-center justify-center rounded-xl border-2 border-dashed border-brand-deep bg-brand/5 text-sm font-medium text-brand-deep">
          Drop files to attach
        </div>
      )}
    </div>
  )
}
